import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import { getDateToDisplay } from "../../../../../utils/nutrition-helpers";
import { deleteMealPlan } from "../../../../../utils/meal-planner-helper";
import { MealPlannerWeekSelector } from "./MealPlannerWeekSelector";
import { defaultFirebaseProjectId } from "../../../../../constants";

export const MealPlannerHeader = ({ mealPlan, locals, planMode }) => {
  const { query, mealPlanIsTemplate, origin, activeUserToRequest, mealPlanLibraries = [] } = locals;
  const { athlete, plan: nutritionPlanId, viewMode, widget } = query;
  const {
    referAsClients = false,
    _id: ownerId,
    avatar: ownerAvatar,
    firebaseProjectId,
    mealPlanAIApproved,
  } = activeUserToRequest;
  const {
    _id: mealPlanId,
    utcStartDate,
    utcEndDate,
    published: mealPlanPublished = false,
    title: mealPlanTitle = "",
    type: mealPlanType,
  } = mealPlan || {};
  let mealPlanLibrary = null;
  if (mealPlanType === "library" && mealPlanId && Array.isArray(mealPlanLibraries)) {
    mealPlanLibrary = mealPlanLibraries.find(
      (lib) => lib.mealPlan?.mealPlanId?.toString() === mealPlanId?.toString()
    );
  }
  const initialPublished =
    mealPlanLibrary ? mealPlanLibrary.status === "published" : mealPlanPublished;
  const isGeneratedWithAI = mealPlanType === "template" && mealPlan?.generation;
  const initialState = {
    published: initialPublished,
    moreOpened: false,
  };
  const [state, setState] = useState(initialState);
  const [mealPlanReview, setMealPlanReview] = useState(null);
  const [isLoadingReview, setIsLoadingReview] = useState(() =>
    Boolean(mealPlanId),
  );
  const { t } = useTranslation("athlete-portal");
  const { published, moreOpened } = state;
  const referName = referAsClients ? "client" : "athlete";

  useEffect(() => {
    if (!mealPlanId || !isGeneratedWithAI) {
      setIsLoadingReview(false);
      return;
    }
    let cancelled = false;
    setIsLoadingReview(true);
    const url = `/actions/meal-planner/meal-plan-review?mealPlanId=${encodeURIComponent(mealPlanId)}`;
    fetch(url, { credentials: "same-origin" })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (ok && data?.rating != null) setMealPlanReview(data);
        setIsLoadingReview(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoadingReview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mealPlanId, isGeneratedWithAI, query?.modal]);

  /**
   * Open modal to create the template
   */
  const handleOpenModal = (section) => {
    const body =
      section === "copy-meal-plan-from-template" && query.week
        ? { "modal[type]": section, week: query.week }
        : { "modal[type]": section };
    submit(`/actions/refresh/?`, {
      method: "GET",
      body,
    });
  };

  /**
   * Function to change the published status of the meal plan
   * @param {*} e
   */
  const handleCheckboxChange = (e) => {
    const value = e.target.checked;
    const action = value ? "publish" : "unpublish";
    if (
      process.browser &&
      confirm(
        action === "publish"
          ? t("mealPlannerHeader.publishConfirm")
          : t("mealPlannerHeader.unpublishConfirm"),
      )
    ) {
      setState((prevState) => ({
        ...prevState,
        published: value,
      }));

      // If the meal plan is a library, update the status of the library
      if (mealPlanType === "library" && mealPlanLibrary?._id) {
        submit(`/actions/meal-plan-library/status/${mealPlanLibrary._id}`, {
          method: "POST",
          body: { status: value ? "published" : "draft" },
        });
        return;
      }

      // If the meal plan is not a library, update the status of the meal plan
      const coachAvatar = ownerAvatar?.file ?? null;

      const firebaseProjectIdToUse =
        firebaseProjectId ?? defaultFirebaseProjectId;

      const body = {
        published: value,
        coachId: ownerId,
        utcStartDate,
        avatar: coachAvatar,
        firebaseProjectId: firebaseProjectIdToUse,
        athleteId: athlete
      };
      submit(
        `/actions/meal-planner/update-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  const handleDelete = () => {
    deleteMealPlan({ mealPlanId, nutritionPlanId, athlete, t });
  };

  return (
    <header
      className={!viewMode ? "meal-planner-header" : "meal-planner-header mb-8"}
    >
      <hgroup className="flex justify-center items-center gap-4">
        <SvgIcon
          src="/img/new-icons/back-button.svg"
          onClickFn={() => {
            if (nutritionPlanId) {
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  section: "",
                  plan: "",
                  mealPlan: "",
                  week: "",
                  tabView: "meal-plans-tab",
                  viewMode: "",
                  templateView: "meal-plans",
                },
              });
            } else if (mealPlanType === "library" && widget) {
              window.location.href = `${origin}/coaching-portal/widgets?widget=${widget}&section=manager&type=meal-plan-library`;
            } else {
              const currentPlanMode = mealPlan?.planMode || "detailed";
              window.location.href = `${origin}/coaching-portal/all-templates?owner=coach&tabView=meal-plans-tab&planMode=${currentPlanMode}&templateView=meal-plans`;
            }
          }}
          classNames="cursor-pointer"
        />

        <div className="flex flex-col gap-1.5">
          {utcStartDate && utcEndDate ? (
            <span className="header-date">
              {getDateToDisplay(utcStartDate)} - {getDateToDisplay(utcEndDate)}
            </span>
          ) : (
            <span className="header-title">{mealPlanTitle}</span>
          )}
        </div>
      </hgroup>
      {!viewMode ? (
        <hgroup className="header-buttons">
          {!(
            (mealPlanType === "template" || !nutritionPlanId) &&
            planMode === "simplified"
          ) && (
            <div className="chip info">
              {planMode === "simplified" &&
              !(mealPlanType === "template" || !nutritionPlanId)
                ? t("mealPlannerHeader.inactiveOptionsNote")
                : t("mealPlannerHeader.inactiveDaysNote")}
            </div>
          )}
          <button
            data-button-type="secondary"
            onClick={() => handleOpenModal("data-source")}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
            <span>{t("mealPlannerHeader.dataSource")}</span>
          </button>

          {isGeneratedWithAI &&
            (mealPlanReview?.rating != null ? (
              <div
                className="meal-plan-rated-badge"
                title={t("mealPlannerHeader.ratedTitle", { rating: mealPlanReview.rating })}
              >
                <span className="meal-plan-rated-badge-icon">
                  <SvgIcon
                    src="/img/new-icons/checkbox-checked.svg"
                    alt=""
                    noFilterOnLight={true}
                    style={{
                      width: "26px",
                      height: "26px",
                      marginBottom: "4px",
                    }}
                  />
                </span>
                <span>{t("mealPlannerHeader.ratedBadge", { rating: mealPlanReview.rating })}</span>
              </div>
            ) : isLoadingReview ? (
              <div
                className="meal-plan-review-loading"
                role="status"
                aria-label={t("mealPlannerHeader.loadingReview")}
              >
                <span className="meal-plan-review-loading-spinner" />
                <span>{t("mealPlannerHeader.loadingReview")}</span>
              </div>
            ) : (
              <button
                type="button"
                data-button-type="primary"
                className="rate-meal-plan-button"
                onClick={() => handleOpenModal("rate-meal-plan")}
              >
                <SvgIcon
                  src="/img/new-icons/thumbs-up-review.svg"
                  alt="thumb up"
                  noFilterOnLight={true}
                  style={{
                    width: "17px",
                    height: "16px",
                    marginBottom: "4px",
                    paddingRight: "2px",
                  }}
                />
                <span>{t("mealPlannerHeader.rateMealPlan")}</span>
              </button>
            ))}

          {nutritionPlanId && planMode === "detailed" && (
            <button
              data-button-type="secondary"
              disabled={mealPlanIsTemplate}
              onClick={() => handleOpenModal("save-as-template")}
            >
              {mealPlanIsTemplate && (
                <SvgIcon
                  src="/img/new-icons/checked-circle.svg"
                  alt="info"
                  noFilterOnLight={true}
                />
              )}
              <span>
                {mealPlanIsTemplate
                  ? t("mealPlannerHeader.savedAsTemplate")
                  : t("mealPlannerHeader.saveAsTemplate")}
              </span>
            </button>
          )}

          {planMode === "detailed" && (
            <button
              data-button-type="secondary"
              onClick={() => handleOpenModal("copy-meal-plan-from-template")}
            >
              <span>{t("mealPlannerHeader.copyFromTemplate")}</span>
            </button>
          )}

          {isGeneratedWithAI && (
            <button
              data-button-type="outline"
              onClick={() => handleOpenModal("modify-with-ai")}
              style={{
                color: "#ffffff",
                background:
                  "linear-gradient(#20262e, #20262e) padding-box, linear-gradient(280deg, #1BA1E3 0%, #1BA1E3 4%, #5489D6 30%, #9B72CB 55%, #D96570 82%, #F49C46 100%) border-box",
                border: "2px solid transparent",
              }}
            >
              <img
                src="/img/favicon/gemini.png"
                alt="gemini icon"
                style={{ width: "20px", height: "20px", marginRight: "6px" }}
              />
              <span style={{ color: "#ffffff" }}>{t("mealPlannerHeader.modifyWithAI")}</span>
            </button>
          )}

          {isGeneratedWithAI && (
            <button
              data-button-type="outline"
              onClick={() => handleOpenModal("regenerate-meal-plan")}
              style={{
                color: "#ffffff",
                background:
                  "linear-gradient(#20262e, #20262e) padding-box, linear-gradient(280deg, #1BA1E3 0%, #1BA1E3 4%, #5489D6 30%, #9B72CB 55%, #D96570 82%, #F49C46 100%) border-box",
                border: "2px solid transparent",
              }}
            >
              <img
                src="/img/favicon/gemini.png"
                alt="gemini icon"
                style={{ width: "20px", height: "20px", marginRight: "6px" }}
              />
              <span style={{ color: "#ffffff" }}>{t("mealPlannerHeader.regenerateWithAI")}</span>
            </button>
          )}

          {(nutritionPlanId || mealPlanType === "library") && (
            <button data-button-type="secondary">
              <span>{t("mealPlannerHeader.publish")}</span>
              <input
                className="ml-12 mr-2"
                type="checkbox"
                id="publish-button"
                name="publish"
                role="switch"
                onChange={handleCheckboxChange}
                checked={published}
              />
              <ToolTip
                id="meal-planner-publish-tooltip"
                className="help-tooltip"
                body={t("mealPlannerHeader.publishTooltip", { referName })}
                multiline
                place="bottom"
              >
                <div className="help-tooltip-container">?</div>
              </ToolTip>
            </button>
          )}

          <details role="list" open>
            <summary
              onClick={(e) => {
                e.preventDefault();
                setState((prevState) => ({
                  ...prevState,
                  moreOpened: !moreOpened,
                }));
              }}
              onBlur={(e) => {
                e.preventDefault();
                setState((prevState) => ({
                  ...prevState,
                  moreOpened: false,
                }));
              }}
              aria-haspopup="listbox"
            >
              {t("mealPlannerHeader.more")}
              <SvgIcon
                src={
                  moreOpened
                    ? "/img/svg/angle-up.svg"
                    : "/img/svg/angle-down.svg"
                }
              />
            </summary>
            <ul role="listbox" className={moreOpened ? "opened" : ""}>
              <li onMouseDown={handleDelete}>
                <span>{t("mealPlannerHeader.deleteMealPlan")}</span>
                <SvgIcon
                  src="/img/new-icons/red-trash.svg"
                  noFilterOnLight={true}
                />
              </li>
            </ul>
          </details>
        </hgroup>
      ) : (
        planMode !== "simplified" && (
          <MealPlannerWeekSelector mealPlan={mealPlan} query={query} />
        )
      )}
    </header>
  );
};

MealPlannerHeader.propTypes = {
  mealPlan: PropTypes.object,
  locals: PropTypes.object,
  planMode: PropTypes.string,
};
